import React, { useState } from 'react';
import { Button } from '@lemon-io/ui-kit';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import RadioButtonCheckedRoundedIcon from '@mui/icons-material/RadioButtonCheckedRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import { CircularProgress, Stack, Typography } from '@mui/material';

import { profileApi } from '@internal/api/profile';
import { JobSearchStatus } from '@internal/types/profile';

import { JOB_SEARCH_OPTIONS } from './signup.constants';
import { OptionRow } from './signup.styled';
import StepDots from './step-dots';

const BasicsStep: React.FC<{
  apiKey: string;
  onBack: () => void;
  onFinish: () => void;
}> = ({ apiKey, onBack, onFinish }) => {
  const [status, setStatus] = useState<JobSearchStatus | null>(null);
  const { updateProfile, updateProfileLoading } =
    profileApi.updateProfile.useMutation();

  const handleContinue = () => {
    if (!status) return;

    updateProfile(
      { apiKey, job_search_status: status },
      { onSettled: onFinish }
    );
  };

  return (
    <>
      <StepDots total={2} current={1} />
      <Typography variant="h4">Where you&apos;re at now?</Typography>
      <Typography variant="body3" sx={{ mt: 1, color: 'text.primary' }}>
        Ready for a new opportunity or just casually browsing?
      </Typography>

      <Typography
        variant="body3"
        sx={{ display: 'block', mt: 4, mb: 2, fontWeight: 500 }}
      >
        Current job-search status
      </Typography>
      <Stack spacing={1.5}>
        {JOB_SEARCH_OPTIONS.map(option => {
          const selected = status === option.value;
          return (
            <OptionRow
              key={option.value}
              onClick={() => setStatus(option.value)}
              $selected={selected}
            >
              {selected ? (
                <RadioButtonCheckedRoundedIcon
                  sx={{ color: 'contrast.main' }}
                />
              ) : (
                <RadioButtonUncheckedRoundedIcon
                  sx={{ color: 'main.grey400' }}
                />
              )}
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {option.label}
              </Typography>
            </OptionRow>
          );
        })}
      </Stack>

      <Stack
        direction={{ xs: 'column-reverse', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={{ xs: 1, sm: 0 }}
        sx={{ mt: 5 }}
      >
        <Button
          variant="text"
          color="contrast"
          icon={<ChevronLeftRoundedIcon />}
          onClick={onBack}
          disabled={updateProfileLoading}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Back
        </Button>
        <Button
          variant="main"
          color="primary"
          disabled={!status || updateProfileLoading}
          icon={
            updateProfileLoading ? (
              <CircularProgress size={16} sx={{ color: 'inherit' }} />
            ) : undefined
          }
          iconPlacement="right"
          onClick={handleContinue}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            whiteSpace: 'nowrap',
            ...(updateProfileLoading && {
              '&.Mui-disabled': { color: 'common.white', opacity: 1 },
            }),
          }}
        >
          {updateProfileLoading ? 'Saving' : 'Build your profile'}
        </Button>
      </Stack>
    </>
  );
};

export default BasicsStep;
